import { Container, IDestroyOptions } from "pixi.js";
import { NFTScrollItem } from "./scrollItems/NFTScrollItem";
import { HBox } from "by/gamefactory/cityversetycoon/scenes/common/components/ui/HBox";
import { gsap } from "gsap";
import { Event } from "event-system";
import { ScrollNextElement } from "./scrollItems/ScrollNextElement";

export class MyWalletScrollBox extends Container {
    public readonly onScroll: Event<number>;
    public readonly onNFTCollectClick: Event<NFTScrollItem>;
    public readonly onItemsCountChange: Event<number>;

    public get items(): ReadonlyArray<NFTScrollItem> {
        return this.scrollItems;
    }

    private static readonly SCROLL_TIME = 0.2;

    private readonly itemsContainer: HBox;
    private readonly animations: gsap.core.Timeline;
    private readonly slideDistance: number;

    private scrollItems: Array<NFTScrollItem>;
    private currentItem?: NFTScrollItem;
    private currentItemIndexValue: number;

    public get itemsCount(): number {
        return this.scrollItems.length;
    }

    public get currentItemIndex(): number {
        return this.currentItemIndexValue;
    }

    public constructor(elementsWidth: number, elementsMargin: number) {
        super();
        this.slideDistance = elementsWidth + elementsMargin;
        this.scrollItems = [];
        this.onScroll = new Event<number>();
        this.onNFTCollectClick = new Event<NFTScrollItem>();
        this.onItemsCountChange = new Event<number>();
        this.animations = new gsap.core.Timeline({ paused: true });
        this.currentItemIndexValue = 0;
        this.itemsContainer = new HBox(elementsMargin);
        this.addChild(this.itemsContainer);
    }

    private onNFTCollectClickHandle(nftScrollItem: NFTScrollItem): void {
        this.onNFTCollectClick.emit(nftScrollItem);
    }

    private async moveItemsContainer(toLeft: boolean): Promise<void> {
        const movedItems = this.scrollItems.filter((_item, index) => toLeft ? index >= this.currentItemIndex : index <= this.currentItemIndex);
        movedItems.forEach((item) => {
            this.animations.fromTo(item.position,
                {
                    x: item.position.x
                },
                {
                    x: toLeft ? item.position.x - this.slideDistance : item.position.x + this.slideDistance,
                    duration: MyWalletScrollBox.SCROLL_TIME,
                    ease: "power1.out"
                }, "<");
        });
        await this.animations.play();
        this.animations.clear();
    }

    private async processHardRemove(removedItemIndex: number): Promise<void> {
        const movedItems = this.items.filter((_item, index) => index >= removedItemIndex);
        for (const item of movedItems) {
            item.position.x -= this.slideDistance;
        }
    }

    public reset(): void {
        if (this.currentItem) {
            this.currentItem.eventMode = 'none';
            this.currentItem = this.scrollItems[0];
            this.currentItem.eventMode = 'static';
            this.currentItemIndexValue = 0;
        }
        this.itemsContainer.position.set(0, 0);
    }

    public addItem(item: NFTScrollItem): void {
        this.scrollItems.push(item);
        this.itemsContainer.addChild(item);
        item.eventMode = 'none';
        if (this.currentItem == null || this.currentItem instanceof ScrollNextElement) {
            this.currentItem = item;
            this.currentItem.eventMode = 'static';
        }
        item.onCollect.add(this.onNFTCollectClickHandle, this);
        this.onItemsCountChange.emit(this.itemsCount);
    }

    public async removeItem(removedItem: NFTScrollItem, hardRemove = false): Promise<void> {
        const removedItemIndex = this.scrollItems.findIndex((item) => item === removedItem);
        this.scrollItems.splice(removedItemIndex, 1);
        this.itemsContainer.removeChild(removedItem);
        removedItem.onCollect.remove(this.onNFTCollectClickHandle, this);
        if (hardRemove) {
            if (this.currentItem === removedItem) {
                this.currentItem = this.scrollItems[0] ?? null;
                if (this.currentItem) {
                    this.currentItem.eventMode = 'static';
                }
                this.currentItemIndexValue = 0;
            }
            this.processHardRemove(removedItemIndex);
            this.onItemsCountChange.emit(this.itemsCount);
            return;
        }
        if (this.itemsCount > 0) {
            if (this.currentItemIndex - 1 !== this.itemsCount - 1) {
                this.currentItem = this.scrollItems[this.currentItemIndex];
                this.currentItem.eventMode = 'static';
                await this.moveItemsContainer(true);

            } else {
                this.currentItemIndexValue--;
                this.currentItem = this.scrollItems[this.currentItemIndex];
                this.currentItem.eventMode = 'static';
                await this.moveItemsContainer(false);

            }
            this.onItemsCountChange.emit(this.itemsCount);
        }
    }

    public async scrollLeft(): Promise<void> {
        this.onScroll.emit(MyWalletScrollBox.SCROLL_TIME);
        this.animations.fromTo(this.itemsContainer.position,
            {
                x: this.itemsContainer.position.x
            },
            {
                x: this.itemsContainer.position.x + this.slideDistance,
                duration: MyWalletScrollBox.SCROLL_TIME,
                ease: "power1.out"
            }
        );
        await this.animations.play();
        this.animations.clear();
        this.currentItem.eventMode = 'none';
        this.currentItemIndexValue--;
        this.currentItem = this.scrollItems[this.currentItemIndexValue];
        this.currentItem.eventMode = 'static';
    }

    public async scrollRight(): Promise<void> {
        this.onScroll.emit(MyWalletScrollBox.SCROLL_TIME);
        this.animations.fromTo(this.itemsContainer.position,
            {
                x: this.itemsContainer.position.x
            },
            {
                x: this.itemsContainer.position.x - this.slideDistance,
                duration: MyWalletScrollBox.SCROLL_TIME,
                ease: "power1.out"
            }
        );
        await this.animations.play();
        this.animations.clear();
        this.currentItem.eventMode = 'none';
        this.currentItemIndexValue++;
        this.currentItem = this.scrollItems[this.currentItemIndexValue];
        this.currentItem.eventMode = 'static';
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.scrollItems.forEach((item) => item.onCollect.remove(this.onNFTCollectClickHandle, this));
        this.animations.kill();
        super.destroy(options);
    }
}